/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

/** @typedef {import('../types').Field} Field */
/** @typedef {import('../types').Table} Table */

import { FormsView } from 'App/Pages/Project/IdpApplication/idp-project/idp-extraction-project/structured-extraction/extracted-results/forms-view';
import { TablesView } from 'App/Pages/Project/IdpApplication/idp-project/idp-extraction-project/structured-extraction/extracted-results/tables-view';
import { ExtractionResultsComparison } from 'App/Pages/Project/IdpApplication/idp-project/idp-extraction-project/structured-extraction/test-template/extraction-results-comparison';

/**
 * @param {{
 *   selectedView: 'forms' | 'tables',
 *   forms?: {
 *     fields: Field[],
 *     onToggleActive?: (id: string, active: boolean) => void,
 *     onToggleAllActive?: (active: boolean) => void,
 *     onNameChange?: (id: string, value: string) => void,
 *     nameValidation?: Record<string, string>,
 *   },
 *   tables?: {
 *     tables: Table[],
 *     onToggleActive?: (id: string, active: boolean) => void,
 *     onToggleAllActive?: (active: boolean) => void,
 *     onNameChange?: (id: string, value: string) => void,
 *   },
 *   comparison?: {
 *     extractedData: object,
 *     templateData: object,
 *   }
 * }} props
 * @returns {JSX.Element}
 */
export function ViewRenderer({ selectedView, forms, tables, comparison }) {
  if (comparison) {
    const comparisonType = selectedView === 'forms' ? 'fields' : 'tables';
    return (
      <ExtractionResultsComparison
        extractedData={comparison.extractedData}
        templateData={comparison.templateData}
        type={comparisonType}
      />
    );
  }
  if (selectedView === 'forms' && forms) {
    return (
      <FormsView
        fields={forms.fields}
        onToggleActive={forms.onToggleActive}
        onToggleAllActive={forms.onToggleAllActive}
        onNameChange={forms.onNameChange}
        nameValidation={forms.nameValidation}
      />
    );
  }

  if (selectedView === 'tables' && tables) {
    return (
      <TablesView
        tables={tables.tables}
        onToggleActive={tables.onToggleActive}
        onToggleAllActive={tables.onToggleAllActive}
        onNameChange={tables.onNameChange}
      />
    );
  }

  return null;
}
